import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Chart from "chart.js/auto";
import {
  getInvoiceReceiptPayment,
  getInvoiceReceiptInfo,
} from "../features/payments/paymentSlice";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { base_url } from "../utils/baseUrl";
import { config } from "../utils/axiosconfig";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";

const PaymentVsReceipt = () => {
  const dispatch = useDispatch();
  const { invoiceReceiptInfo, isLoading } = useSelector(
    (state) => state.payment
  );
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [filters, setFilters] = useState({
    year: "",
    month: "",
    state: "",
    district: "",
    vc: "",
    fromDate: "",
    toDate: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceSearchInput, setInvoiceSearchInput] = useState(""); // Input field value for search
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [gapMetrics, setGapMetrics] = useState({
    avgSalaryDueToPaid: 0,
    avgSalaryPaidToBillGen: 0,
    avgBillGenToSubmission: 0,
  });
  const [gapMetricsLoading, setGapMetricsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedInvoiceNos, setSelectedInvoiceNos] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredGapCard, setHoveredGapCard] = useState(null);
  
  // Calculate gap metrics from filtered data (frontend calculation)
  const calculateGapMetricsFromData = (data) => {
    if (!data || data.length === 0) {
      return {
        avgSalaryDueToPaid: 0,
        avgSalaryPaidToBillGen: 0,
        avgBillGenToSubmission: 0,
      };
    }

    // This would need actual date fields from the data
    // For now, using backend API is better
    // But we'll trigger refetch when invoice search changes
    return gapMetrics;
  };

  const displayData = selectedInvoiceNos.length > 0
    ? filteredData.filter((item) => selectedInvoiceNos.includes(item.invoice_no))
    : invoiceSearch 
    ? filteredData.filter((item) => 
        (item.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
      )
    : filteredData;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const normalizeState = (state) => {
    if (!state) return { display: "N/A", code: "N/A" };
    const match = state.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      return { display: state, code: match[2].trim() };
    }
    return { display: state, code: state };
  };

  useEffect(() => {
    dispatch(getInvoiceReceiptPayment());
    dispatch(getInvoiceReceiptInfo());
  }, [dispatch]);

  useEffect(() => {
    console.log('🎯 [PaymentVsReceipt] Gap Metrics useEffect triggered!', {
      invoiceSearch,
      filtersState: filters.state,
      filtersMonth: filters.month,
      filteredDataCount: filteredData.length,
      refreshTrigger,
      selectedInvoiceNosCount: selectedInvoiceNos.length
    });
    
    // Update gap metrics when filters change OR when filtered data changes
    const fetchGapMetrics = async () => {
      setGapMetricsLoading(true);
      try {
        // Build query string from filters
        const queryParams = new URLSearchParams();
        if (filters.state) queryParams.append('state', normalizeState(filters.state).display);
        if (filters.month) queryParams.append('month', filters.month);
        if (filters.year) queryParams.append('year', filters.year);
        if (filters.vc) queryParams.append('vc_name', filters.vc);
        
        // Priority 1: If rows are selected
        if (selectedInvoiceNos.length > 0) {
          queryParams.append('invoice_numbers', selectedInvoiceNos.join(','));
        }
        // Priority 2: If invoice search is active
        else if (invoiceSearch) {
          const filteredInvoiceNumbers = filteredData
            .filter((item) => 
              (item.invoice_no || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
            )
            .map((item) => item.invoice_no)
            .filter(Boolean);
          
          if (filteredInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', filteredInvoiceNumbers.join(','));
          } else {
            // If invoice search is active but no matches found, send a dummy value
            console.warn('⚠️ [PaymentVsReceipt] No matching invoices found for search:', invoiceSearch);
            queryParams.append('invoice_numbers', '__NO_MATCH__');
          }
        }
        // Priority 3: Otherwise use all filtered data
        else if (filteredData && filteredData.length > 0) {
          const allInvoiceNumbers = filteredData
            .map((item) => item.invoice_no)
            .filter(Boolean);
          
          if (allInvoiceNumbers.length > 0) {
            queryParams.append('invoice_numbers', allInvoiceNumbers.join(','));
          }
        }
        
        const queryString = queryParams.toString();
        const url = `${base_url}invoince/gapMetrics${queryString ? `?${queryString}` : ''}`;
        
        console.log('📡 [PaymentVsReceipt] Fetching gap metrics from:', url);
        
        const response = await fetch(url, {
          headers: config.headers
        });
        
        const data = await response.json();
        console.log('📊 [PaymentVsReceipt] Gap metrics response:', data);
        
        if (data.success && data.data) {
          console.log('✅ [PaymentVsReceipt] Setting gap metrics to:', data.data);
          setGapMetrics({
            avgSalaryDueToPaid: data.data.avgSalaryDueToPaid !== null ? data.data.avgSalaryDueToPaid : null,
            avgSalaryPaidToBillGen: data.data.avgSalaryPaidToBillGen !== null ? data.data.avgSalaryPaidToBillGen : null,
            avgBillGenToSubmission: data.data.avgBillGenToSubmission !== null ? data.data.avgBillGenToSubmission : null,
            _timestamp: Date.now(),
          });
        } else {
          console.error('⚠️ [PaymentVsReceipt] Gap metrics API returned unsuccessful response:', data);
        }
      } catch (error) {
        console.error('❌ [PaymentVsReceipt] Failed to fetch gap metrics:', error);
      } finally {
        setGapMetricsLoading(false);
      }
    };
    
    // Always call fetchGapMetrics, even if filteredData is empty (for invoice search with no results)
    console.log('✅ [PaymentVsReceipt] Calling fetchGapMetrics...');
    fetchGapMetrics();
  }, [filters.state, filters.month, filters.year, filters.vc, filteredData, invoiceSearch, selectedInvoiceNos, refreshTrigger]);
 
  useEffect(() => {
    if (invoiceReceiptInfo?.data?.length > 0) {
      setFilteredData(invoiceReceiptInfo.data);
      updateChart(invoiceReceiptInfo.data);
    }
  }, [invoiceReceiptInfo]);
 
  // Update chart when invoice search or selection changes
  useEffect(() => {
    if (filteredData.length > 0) {
      updateChart(displayData);
    }
  }, [invoiceSearch, filteredData, selectedInvoiceNos]);

  const updateChart = (data) => {
    const ctx = chartRef.current?.getContext("2d");
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const states = data.map(
      (item) => item.invoice_info?._id?.state || "Unknown"
    );
    const totalSalaryPaid = data.map((item) => item.total_salary_paid || 0);
    const invoiceAmount = data.map((item) => item.total_invoice_amount || 0);
    const paymentReceived = data.map(
      (item) => item.total_payment_received || 0
    );
    const totalPaymentReceivedInclTDS = data.map(
      (item) =>
        (item.total_payment_received || 0) +
        (item?.invoice_info?.total_tds || 0)
    );

    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: states,
        datasets: [
          {
            label: "Total Salary Paid (₹)",
            data: totalSalaryPaid,
            backgroundColor: "#3b82f6",
          },
          {
            label: "Invoice Amount (₹)",
            data: invoiceAmount,
            backgroundColor: "#22c55e",
          },
          {
            label: "Payment Received Excl. TDS (₹)",
            data: paymentReceived,
            backgroundColor: "#f97316",
          },
          {
            label: "Total Payment Received Incl. TDS (₹)",
            data: totalPaymentReceivedInclTDS,
            backgroundColor: "#6f49c9",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              label: function (tooltipItem) {
                return `${
                  tooltipItem.dataset.label
                }: ₹${tooltipItem.raw.toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: {
            title: { display: true, text: "States" },
            ticks: {
              autoSkip: true,
              maxRotation: isMobile ? 90 : 45,
              minRotation: isMobile ? 90 : 0,
              font: { size: isMobile ? 8 : 12 },
            },
          },
          y: {
            title: { display: true, text: "Amount (₹)" },
            beginAtZero: true,
            ticks: { font: { size: isMobile ? 8 : 12 } },
          },
        },
      },
    });
  };

  const formatCurrency = (amount) =>
    `₹ ${Number(amount || 0).toLocaleString("en-IN")}`;

  const overviewData =
    displayData.length > 0
      ? [
          {
            label: "Total Salary Paid (₹)",
            value: formatCurrency(
              displayData.reduce(
                (sum, item) => sum + (item.total_salary_paid || 0),
                0
              )
            ),
            color: "#3b82f6",
            tag: "Data from Payroll",
          },
          {
            label: "Total Invoice Amount (₹)",
            value: formatCurrency(
              displayData.reduce(
                (sum, item) => sum + (item.total_invoice_amount || 0),
                0
              )
            ),
            color: "#22c55e",
            tag: "Data from Invoice",
          },
          {
            label: "Deductions (₹)",
            value: formatCurrency(
              displayData.reduce(
                (sum, item) => sum + (item?.invoice_info?.total_deductions || 0),
                0
              )
            ),
            color: "#8b5cf6",
            tag: "Data from Invoice",
          },
          {
            label: "Total Payment Received Incl. TDS (₹)",
            value: formatCurrency(
              displayData.reduce(
                (sum, item) =>
                  sum +
                  (item.total_payment_received || 0) +
                  (item?.invoice_info?.total_tds || 0),
                0
              )
            ),
            color: "#6f49c9",
            tag: "Data from Invoice",
          },
          {
            label: "Excess Receipt",
            value: formatCurrency(
              displayData.reduce(
                (sum, item) => sum + (item.excess_receipt || 0),
                0
              )
            ),
            color: "#f97316",
            tag: "Calculated",
          },
          {
            label: "Excess Receipt % on Salary Paid",
            value: (() => {
              const totalSalaryPaid = displayData.reduce(
                (sum, item) => sum + (item.total_salary_paid || 0),
                0
              );
              const totalExcessReceipt = displayData.reduce(
                (sum, item) =>
                  sum +
                  ((item.invoice_info.total_payment_received || 0) +
                    (item.invoice_info.total_tds || 0) -
                    (item.total_salary_paid || 0)),
                0
              );
              return totalSalaryPaid
                ? `${((totalExcessReceipt / totalSalaryPaid) * 100).toFixed(
                    2
                  )}%`
                : "0%";
            })(),
            color: "#ef4444",
            tag: "Calculated from Receipts",
          },
        ]
      : [
          {
            label: "Total Salary Paid (₹)",
            value: "₹ 0",
            color: "#3b82f6",
            tag: "Data from Payroll",
          },
          {
            label: "Total Invoice Amount (₹)",
            value: "₹ 0",
            color: "#22c55e",
            tag: "Data from Invoice",
          },
          {
            label: "Deductions (₹)",
            value: "₹ 0",
            color: "#8b5cf6",
            tag: "Data from Invoice",
          },
          {
            label: "Total Payment Received Incl. TDS (₹)",
            value: "₹ 0",
            color: "#6f49c9",
            tag: "Data from Invoice",
          },
          {
            label: "Excess Receipt",
            value: "₹ 0",
            color: "#f97316",
            tag: "Calculated",
          },
          {
            label: "Excess Receipt % on Salary Paid",
            value: "0%",
            color: "#ef4444",
            tag: "Calculated from Receipts",
          },
        ];

  const tableRows = filteredData.map((item) => ({
    invoiceNo: item.invoice_no || "N/A",
    month: item.salary_month || "N/A",
    year: item.year || "N/A",
    state: item.invoice_info?._id?.state || "N/A",
    district: item.invoice_info?._id?.district || "N/A",
    vc_name: item.vc_name || "N/A",
    totalSalaryPaid: formatCurrency(item.total_salary_paid),
    invoiceAmount: formatCurrency(item.total_invoice_amount),
    paymentReceived: formatCurrency(item.total_payment_received),
    tdsDeducted: formatCurrency(item.invoice_info?.total_tds),
    deductions: formatCurrency(item.invoice_info?.total_deductions),
    excessReceipt: formatCurrency(item.excess_receipt),
    excessReceiptPercent: item.express_receipt_percent
      ? `${Number(item.express_receipt_percent).toFixed(3)}%`
      : item.total_salary_paid
      ? `${Number((item.excess_receipt / item.total_salary_paid) * 100).toFixed(
          3
        )}%`
      : "0.000%",
  }));

  const uniqueYears = Array.from(
    new Set(filteredData.map((item) => item.year || "N/A"))
  );
  const uniqueMonths = Array.from(
    new Set(filteredData.map((item) => item.salary_month || "N/A"))
  );
  const uniqueStates = Array.from(
    new Set(
      filteredData.map((item) => {
        const state = item.invoice_info?._id?.state || "N/A";
        return normalizeState(state).display;
      })
    )
  ).filter((s) => s !== "N/A").sort();
  const uniqueDistricts = Array.from(
    new Set(
      filteredData.map((item) => item.invoice_info?._id?.district || "N/A")
    )
  ).sort();
  const uniqueVCs = Array.from(
    new Set(filteredData.map((item) => item.vc_name || "N/A"))
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const filtered = (invoiceReceiptInfo?.data || []).filter((item) => {
      const state = item.invoice_info?._id?.state || "N/A";
      const normalizedState = normalizeState(state);
      const stateMatches =
        !filters.state ||
        normalizedState.display === filters.state ||
        normalizedState.code === filters.state;

      // Date range filter based on invoice_date
      let dateMatch = true;
      if (filters.fromDate || filters.toDate) {
        const invoiceDate = item.invoice_info?.invoice_date ? new Date(item.invoice_info.invoice_date) : null;
        
        if (invoiceDate) {
          if (filters.fromDate) {
            const fromDate = new Date(filters.fromDate);
            dateMatch = dateMatch && invoiceDate >= fromDate;
          }
          if (filters.toDate) {
            const toDate = new Date(filters.toDate);
            toDate.setHours(23, 59, 59, 999); // Include the entire end date
            dateMatch = dateMatch && invoiceDate <= toDate;
          }
        } else {
          // If no invoiceDate and date filter is active, exclude this record
          dateMatch = false;
        }
      }

      return (
        (!filters.year || item.year === filters.year) &&
        (!filters.month || item.salary_month === filters.month) &&
        stateMatches &&
        (!filters.district ||
          item.invoice_info?._id?.district === filters.district) &&
        (!filters.vc || item.vc_name === filters.vc) &&
        dateMatch
      );
    });
    setFilteredData(filtered);
    updateChart(filtered);
    setCurrentPage(0);
  };

  const resetFilters = () => {
    setFilters({
      year: "",
      month: "",
      state: "",
      district: "",
      vc: "",
      fromDate: "",
      toDate: "",
    });
    setFilteredData(invoiceReceiptInfo?.data || []);
    updateChart(invoiceReceiptInfo?.data || []);
    setSelectedInvoiceNos([]);
    setCurrentPage(0);
  };

  const handleSelectRow = (invoiceNo) => {
    if (!invoiceNo || invoiceNo === "N/A") return;
    setSelectedInvoiceNos((prev) =>
      prev.includes(invoiceNo)
        ? prev.filter((no) => no !== invoiceNo)
        : [...prev, invoiceNo]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageInvoiceNos = paginatedRows
        .map((row) => row.invoiceNo)
        .filter((no) => no && no !== "N/A");
      setSelectedInvoiceNos((prev) => [
        ...new Set([...prev, ...currentPageInvoiceNos]),
      ]);
    } else {
      const currentPageInvoiceNos = paginatedRows
        .map((row) => row.invoiceNo)
        .filter((no) => no && no !== "N/A");
      setSelectedInvoiceNos((prev) =>
        prev.filter((no) => !currentPageInvoiceNos.includes(no))
      );
    }
  };

  const handleSort = (key, index) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      let aValue, bValue;
      switch (index) {
        case 1:
          aValue = a.invoice_no || "";
          bValue = b.invoice_no || "";
          break;
        case 2:
          aValue = a.salary_month || "";
          bValue = b.salary_month || "";
          break;
        case 3:
          aValue = a.year || "";
          bValue = b.year || "";
          break;
        case 4:
          aValue = a.invoice_info?._id?.state || "";
          bValue = b.invoice_info?._id?.state || "";
          break;
        case 5:
          aValue = a.invoice_info?._id?.district || "";
          bValue = b.invoice_info?._id?.district || "";
          break;
        case 6:
          aValue = a.vc_name || "";
          bValue = b.vc_name || "";
          break;
        case 7:
          aValue = Number(a.total_salary_paid) || 0;
          bValue = Number(b.total_salary_paid) || 0;
          break;
        case 8:
          aValue = Number(a.total_invoice_amount) || 0;
          bValue = Number(b.total_invoice_amount) || 0;
          break;
        case 9:
          aValue = Number(a.total_payment_received) || 0;
          bValue = Number(b.total_payment_received) || 0;
          break;
        case 10:
          aValue = Number(a.tds_deducted || a.total_tds) || 0;
          bValue = Number(b.tds_deducted || b.total_tds) || 0;
          break;
        case 11:
          aValue =
            (Number(a.total_payment_received) || 0) +
            (Number(a.tds_deducted || a.total_tds) || 0);
          bValue =
            (Number(b.total_payment_received) || 0) +
            (Number(b.tds_deducted || b.total_tds) || 0);
          break;
        case 12:
          aValue = Number(a.total_deductions) || 0;
          bValue = Number(b.total_deductions) || 0;
          break;
        case 13:
          aValue = Number(a.excess_receipt) || 0;
          bValue = Number(b.excess_receipt) || 0;
          break;
        case 14:
          aValue =
            a.express_receipt_percent ||
            (a.total_salary_paid
              ? (a.excess_receipt / a.total_salary_paid) * 100
              : 0);
          bValue =
            b.express_receipt_percent ||
            (b.total_salary_paid
              ? (b.excess_receipt / b.total_salary_paid) * 100
              : 0);
          break;
        default:
          aValue = "";
          bValue = "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredData(sortedData);
    setCurrentPage(0);
  };

  const paginatedRows = tableRows
    .filter((row) => 
      !invoiceSearch || 
      (row.invoiceNo || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
    )
    .slice(
      currentPage * rowsPerPage,
      currentPage * rowsPerPage + rowsPerPage
    );
  const totalRows = tableRows.filter((row) => 
    !invoiceSearch || 
    (row.invoiceNo || "").toLowerCase() === invoiceSearch.toLowerCase().trim()
  ).length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const exportToCSV = () => {
    // Define headers as per your table
    const headers = [
      "Invoice Number",
      "Month",
      "Year",
      "State",
      "District",
      "VC Name",
      "Total Salary Paid",
      "Invoice Amount",
      "Payment Received Excl. TDS",
      "TDS",
      "Payment Received Incl. TDS",
      "Deductions",
      "Excess Receipt",
      "Excess Receipt % on Salary Paid",
    ];

    // Helper: Remove formatting and return raw number
    const getRawNumber = (formattedValue) => {
      if (!formattedValue) return "0";
      return formattedValue.replace(/[^0-9.-]+/g, "");
    };

    // Helper: Escape quotes for CSV
    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    // Prepare CSV rows
    const csvRows = tableRows.map((row) => [
      escapeCsv(row.invoiceNo || ""),
      escapeCsv(row.month || ""),
      escapeCsv(row.year || ""),
      escapeCsv(row.state || ""),
      escapeCsv(row.district || ""),
      escapeCsv(row.vc_name || ""),
      escapeCsv(getRawNumber(row.totalSalaryPaid)),
      escapeCsv(getRawNumber(row.invoiceAmount)),
      escapeCsv(getRawNumber(row.paymentReceived)),
      escapeCsv(getRawNumber(row.tdsDeducted)),
      escapeCsv(
        String(
          Number(getRawNumber(row.tdsDeducted)) +
            Number(getRawNumber(row.paymentReceived))
        )
      ),
      escapeCsv(getRawNumber(row.deductions)),
      escapeCsv(getRawNumber(row.excessReceipt)),
      escapeCsv(row.excessReceiptPercent || "0"),
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.map((header) => escapeCsv(header)).join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "payment_vs_receipt.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    
    doc.setFont("helvetica");

    doc.setFontSize(18);
    doc.text("Payment vs Receipt Trends", 14, 20);

    doc.setFontSize(12);
    doc.text("Overview", 14, 30);
    
    // Format numbers for PDF
    const formatPDFNumber = (value) => {
      if (value === null || value === undefined || value === "") return "0";
      const num = parseFloat(value);
      if (isNaN(num)) return "0";
      return num.toFixed(0);
    };
    
    const cardData = overviewData.map((item) => {
      let formattedValue = item.value;
      // If value contains rupee symbol, format it
      if (typeof formattedValue === 'string' && formattedValue.includes('₹')) {
        const numValue = formattedValue.replace(/[₹,\s]/g, '');
        formattedValue = `Rs ${formatPDFNumber(numValue)}`;
      }
      // Clean up label - remove (₹) and (') symbols
      const cleanLabel = item.label.replace(/\s*\(₹\)\s*/g, '').replace(/\s*\('\)\s*/g, '');
      return [cleanLabel, formattedValue, item.tag];
    });
    
    doc.autoTable({
      startY: 35,
      head: [["Metric", "Value", "Source"]],
      body: cardData,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2, font: "helvetica" },
      headStyles: { fillColor: [59, 130, 246] },
    });

    const chartCanvas = chartRef.current;
    const chartImage = chartCanvas.toDataURL("image/png");
    const chartYPosition = doc.lastAutoTable.finalY + 5;
    doc.addImage(
      chartImage,
      "PNG",
      14,
      chartYPosition,
      isMobile ? 250 : 270,
      isMobile ? 60 : 80
    );

    const headers = [
      "Invoice Number",
      "Month",
      "Year",
      "State",
      "District",
      "VC Name",
      "Total Salary Paid",
      "Invoice Amount",
      "Payment Received Excl. TDS",
      "TDS",
      "Payment Received Incl. TDS",
      "Deductions",
      "Excess Receipt",
      "Excess Receipt %",
    ];
    
    const data = tableRows.map((row) => {
      const formatRowValue = (val) => {
        if (typeof val === 'string' && val.includes('₹')) {
          const numValue = val.replace(/[₹,\s]/g, '');
          return `Rs ${formatPDFNumber(numValue)}`;
        }
        return val;
      };
      
      const tdsNum = Number(row.tdsDeducted.replace(/[^0-9.-]+/g, ""));
      const paymentNum = Number(row.paymentReceived.replace(/[^0-9.-]+/g, ""));
      
      return [
        row.invoiceNo,
        row.month,
        row.year,
        row.state,
        row.district,
        row.vc_name,
        formatRowValue(row.totalSalaryPaid),
        formatRowValue(row.invoiceAmount),
        formatRowValue(row.paymentReceived),
        formatRowValue(row.tdsDeducted),
        `Rs ${formatPDFNumber(tdsNum + paymentNum)}`,
        formatRowValue(row.deductions),
        formatRowValue(row.excessReceipt),
        row.excessReceiptPercent,
      ];
    });

    doc.autoTable({
      startY: chartYPosition + (isMobile ? 65 : 85),
      head: [headers],
      body: data,
      theme: "striped",
      styles: { 
        fontSize: 5.5, 
        cellPadding: 1,
        font: "helvetica",
        overflow: "linebreak",
        cellWidth: "wrap",
        halign: "left"
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 5.5,
        fontStyle: "bold",
        halign: "center"
      },
      margin: { left: 5, right: 5 },
      columnStyles: {
        0: { cellWidth: 20 },  // Invoice Number
        1: { cellWidth: 14 },  // Month
        2: { cellWidth: 10 },  // Year
        3: { cellWidth: 16 },  // State
        4: { cellWidth: 12 },  // District
        5: { cellWidth: 18 },  // VC Name
        6: { cellWidth: 18 },  // Total Salary Paid
        7: { cellWidth: 18 },  // Invoice Amount
        8: { cellWidth: 20 },  // Payment Received Excl. TDS
        9: { cellWidth: 14 },  // TDS
        10: { cellWidth: 20 }, // Payment Received Incl. TDS
        11: { cellWidth: 16 }, // Deductions
        12: { cellWidth: 16 }, // Excess Receipt
        13: { cellWidth: 14 }  // Excess Receipt %
      },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(
          `Page ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      },
    });

    doc.save("payment_vs_receipt.pdf");
  };

  const handlePrint = () => {
    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment vs Receipt Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
            padding: 40px;
            background: #ffffff;
            color: #1a2332;
          }
          .report-header {
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #eef2f6;
          }
          .report-title {
            font-size: 24px;
            font-weight: 700;
            color: #1a2332;
            margin-bottom: 4px;
          }
          .report-subtitle {
            font-size: 14px;
            color: #64748b;
          }
          .report-meta {
            display: flex;
            gap: 20px;
            margin-top: 8px;
            font-size: 13px;
            color: #475569;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 16px;
            margin: 24px 0;
          }
          .summary-card {
            background: #f8fafc;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #eef2f6;
            text-align: center;
          }
          .summary-label {
            font-size: 12px;
            color: #64748b;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }
          .summary-value {
            font-size: 16px;
            font-weight: 700;
            color: #1a2332;
            margin-top: 4px;
          }
          .summary-value.blue { color: #3b82f6; }
          .summary-value.green { color: #10b981; }
          .summary-value.purple { color: #8b5cf6; }
          .summary-value.deep-purple { color: #6f49c9; }
          .summary-value.orange { color: #f97316; }
          .summary-value.red { color: #ef4444; }
          
          .gap-metrics {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin: 24px 0;
          }
          .metric-card {
            background: #f8fafc;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #eef2f6;
            text-align: center;
          }
          .metric-label {
            font-size: 12px;
            color: #64748b;
            font-weight: 500;
          }
          .metric-value {
            font-size: 18px;
            font-weight: 700;
            color: #1a2332;
            margin-top: 4px;
          }
          
          .table-container {
            margin-top: 24px;
            overflow-x: auto;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
          }
          thead {
            background: #f8fafc;
          }
          th {
            padding: 8px 10px;
            text-align: left;
            font-weight: 600;
            color: #475569;
            border-bottom: 2px solid #eef2f6;
            white-space: nowrap;
            text-transform: uppercase;
            font-size: 8px;
            letter-spacing: 0.03em;
          }
          td {
            padding: 6px 10px;
            border-bottom: 1px solid #f0f0f0;
            color: #1a2332;
          }
          tr:hover {
            background: #fafbfc;
          }
          .text-right {
            text-align: right;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #eef2f6;
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #94a3b8;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
            .summary-grid { break-inside: avoid; }
            .gap-metrics { break-inside: avoid; }
            table { break-inside: auto; }
            tr { break-inside: avoid; }
          }
          @media (max-width: 768px) {
            .summary-grid { grid-template-columns: repeat(3, 1fr); }
            .gap-metrics { grid-template-columns: 1fr; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-title">📊 Payment vs Receipt Report</div>
          <div class="report-subtitle">Comprehensive analysis of salary paid vs invoice receipts</div>
          <div class="report-meta">
            <span>📅 Generated: ${currentDate}</span>
            <span>📋 Records: ${displayData.length}</span>
          </div>
        </div>

        <div class="summary-grid">
          ${overviewData.map((item, idx) => {
            const colors = ["blue", "green", "purple", "deep-purple", "orange", "red"];
            const colorClass = colors[idx] || "blue";
            return `
              <div class="summary-card">
                <div class="summary-label">${item.label}</div>
                <div class="summary-value ${colorClass}">${item.value}</div>
              </div>
            `;
          }).join('')}
        </div>

        <div class="gap-metrics">
          ${[
            { label: "Salary Due → Paid", value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A" },
            { label: "Salary Paid → Bill Gen", value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A" },
            { label: "Bill Gen → Submission", value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A" }
          ].map(item => `
            <div class="metric-card">
              <div class="metric-label">${item.label}</div>
              <div class="metric-value">${item.value}</div>
            </div>
          `).join('')}
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Invoice Number</th>
                <th>Month</th>
                <th>Year</th>
                <th>State</th>
                <th>District</th>
                <th>VC Name</th>
                <th>Total Salary Paid</th>
                <th>Invoice Amount</th>
                <th>Payment Received Excl. TDS</th>
                <th>TDS</th>
                <th>Payment Received Incl. TDS</th>
                <th>Deductions</th>
                <th>Excess Receipt</th>
                <th>Excess Receipt %</th>
              </tr>
            </thead>
            <tbody>
              ${displayData.map((item, index) => {
                const tdsVal = (item.invoice_info?.total_tds || 0);
                const paymentVal = (item.total_payment_received || 0);
                const paymentInclTds = tdsVal + paymentVal;
                
                const salaryPaid = item.total_salary_paid || 0;
                const excessReceiptPercent = salaryPaid
                  ? `${((item.excess_receipt / salaryPaid) * 100).toFixed(3)}%`
                  : "0.000%";

                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.invoice_no || "N/A"}</td>
                    <td>${item.salary_month || "N/A"}</td>
                    <td>${item.year || "N/A"}</td>
                    <td>${item.invoice_info?._id?.state || "N/A"}</td>
                    <td>${item.invoice_info?._id?.district || "N/A"}</td>
                    <td>${item.vc_name || "N/A"}</td>
                    <td class="text-right">₹${(item.total_salary_paid || 0).toLocaleString()}</td>
                    <td class="text-right">₹${(item.total_invoice_amount || 0).toLocaleString()}</td>
                    <td class="text-right">₹${(item.total_payment_received || 0).toLocaleString()}</td>
                    <td class="text-right">₹${(item.invoice_info?.total_tds || 0).toLocaleString()}</td>
                    <td class="text-right">₹${paymentInclTds.toLocaleString()}</td>
                    <td class="text-right">₹${(item.invoice_info?.total_deductions || 0).toLocaleString()}</td>
                    <td class="text-right">₹${(item.excess_receipt || 0).toLocaleString()}</td>
                    <td>${excessReceiptPercent}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <span>Generated on ${new Date().toLocaleString()}</span>
          <span>${displayData.length} records</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } else {
      alert('Please allow pop-ups for printing');
    }
  };

  if (isLoading) return <div>Loading...</div>;

  // Styles
  const containerStyle = {
    backgroundColor: "#eaedf2",
    padding: isMobile ? "12px" : "14px",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
    fontFamily: "Poppins, sans-serif",
  };

  const wrapperStyle = {
    maxWidth: isMobile ? "100%" : "1200px",
    margin: "0 auto",
    width: "100%",
  };

  const overviewGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(6, 1fr)",
    gap: isMobile ? "12px" : "16px",
    marginBottom: isMobile ? "12px" : "24px",
  };

  const cardStyle = {
    backgroundColor: "",
    color: "#ffffff",
    padding: isMobile ? "12px" : "16px 14px",
    borderRadius: "10px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    position: "relative",
    width: "100%",
    minHeight: isMobile ? "110px" : "140px",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  };

  const tagStyle = {
    position: "absolute",
    top: "8px",
    right: "8px",
    backgroundColor: "#1f2937",
    padding: isMobile ? "2px 4px" : "3px 6px",
    fontSize: isMobile ? "8px" : "9px",
    borderRadius: "4px",
  };

  const filtersGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(6, 1fr)",
    gap: isMobile ? "8px" : "16px",
    marginBottom: isMobile ? "12px" : "24px",
    backgroundColor: "#f8fafc",
    padding: isMobile ? "12px" : "16px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  };

  const selectStyle = {
    border: "1px solid #e2e8f0",
    borderRadius: "4px",
    padding: isMobile ? "6px" : "8px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    width: "100%",
    fontSize: isMobile ? "12px" : "14px",
  };

  const buttonStyle = {
    backgroundColor: "",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    padding: isMobile ? "6px 12px" : "8px 16px",
    fontSize: isMobile ? "12px" : "14px",
    width: isMobile ? "100%" : "auto",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  };

  const chartSectionStyle = {
    backgroundColor: "#f8fafc",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    marginBottom: isMobile ? "12px" : "24px",
  };

  const chartContainerStyle = {
    position: "relative",
    width: "100%",
    height: isMobile ? "250px" : "400px",
    overflowX: "hidden",
  };

  const buttonContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "1rem",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "12px" : "0",
  };

  const tableSectionStyle = {
    backgroundColor: "#f8fafc",
    padding: isMobile ? "12px" : "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    overflowX: "auto",
  };

  const tableWrapperStyle = {
    width: "100%",
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "600px",
    position: "relative",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: isMobile ? "800px" : "auto",
    fontSize: isMobile ? "12px" : "14px",
  };

  const thStyle = {
    border: "1px solid #d1d5db",
    padding: isMobile ? "8px" : "8px",
    textAlign: "left",
    whiteSpace: "nowrap",
    cursor: "pointer",
    backgroundColor: "#edf2f7",
    position: "sticky",
    top: 0,
    zIndex: 10,
  };

  const tdStyle = {
    border: "1px solid #d1d5db",
    padding: isMobile ? "8px" : "8px",
    whiteSpace: "nowrap",
  };

  const paginationStyle = {
    marginTop: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "8px" : "0",
  };

  const footerStyle = {
    textAlign: "center",
    color: "#6b7280",
    marginTop: isMobile ? "20px" : "1.5rem",
    fontSize: isMobile ? "12px" : "14px",
  };

  return (
    <div style={containerStyle}>
      <div style={wrapperStyle}>
        {/* Overview Cards */}
        <div style={overviewGridStyle}>
          {overviewData.map((item, index) => {
            const isHovered = hoveredCard === item.label;
            return (
              <div
                key={index}
                style={{
                  ...cardStyle,
                  backgroundColor: item.color,
                  transform: isHovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
                  boxShadow: isHovered 
                    ? "0 12px 35px rgba(0, 0, 0, 0.25)" 
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                  borderColor: isHovered ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.15)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredCard(item.label)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Background overlay on hover */}
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isHovered 
                    ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" 
                    : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%)",
                  transition: "all 0.3s ease",
                  pointerEvents: "none",
                }} />

                <div style={tagStyle}>{item.tag}</div>
                <h6 style={{ 
                  margin: 0, 
                  fontSize: isMobile ? "11px" : "13px", 
                  fontWeight: "500",
                  opacity: 0.9,
                  position: "relative",
                  zIndex: 1,
                  textAlign: "left",
                  paddingRight: isMobile ? "55px" : "85px",
                  lineHeight: "1.3"
                }}>
                  {item.label}
                </h6>
                <p style={{ 
                  fontSize: isMobile ? "14px" : "20px", 
                  margin: "8px 0 0", 
                  fontWeight: "bold",
                  position: "relative",
                  zIndex: 1,
                  textAlign: "left",
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  marginTop: "auto"
                }}>
                  {item.value}
                </p>

                {/* Bottom accent line on hover */}
                <div style={{
                  position: "absolute",
                  bottom: 0,
                  left: isHovered ? "10%" : "50%",
                  right: isHovered ? "10%" : "50%",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.4)",
                  borderRadius: "2px",
                  transition: "all 0.3s ease",
                  opacity: isHovered ? 1 : 0,
                }} />
              </div>
            );
          })}
        </div>

        {/* Gap Metrics Section */}
        <div style={{
          backgroundColor: "#f8fafc",
          padding: isMobile ? "16px" : "24px",
          borderRadius: "12px",
          border: "1px solid #eef2f6",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          marginBottom: "24px",
        }}>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}>
            <h2 style={{
              fontWeight: "700",
              fontSize: isMobile ? "16px" : "18px",
              margin: 0,
              color: "#1a2332",
              letterSpacing: "-0.01em",
            }}>
              Average Gap Metrics
            </h2>
            <button
              onClick={() => {
                console.log('🔄 [PaymentVsReceipt] Manual refresh triggered');
                setRefreshTrigger(prev => prev + 1); // Increment to trigger useEffect
              }}
              style={{
                backgroundColor: "#3b82f6",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 16px",
                fontSize: "14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#2563eb"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "#3b82f6"}
              disabled={gapMetricsLoading}
            >
              <span style={{ fontSize: "16px" }}>🔄</span>
              {gapMetricsLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "20px",
          }}>
            {gapMetricsLoading ? (
              <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }} />
                <p style={{ marginTop: "10px", color: "#6b7280" }}>Loading...</p>
              </div>
            ) : (
              [
                {
                  label: "Salary Due → Paid",
                  value: gapMetrics.avgSalaryDueToPaid !== null ? `${gapMetrics.avgSalaryDueToPaid} days` : "N/A",
                  color: gapMetrics.avgSalaryDueToPaid === null ? "#9ca3af" : gapMetrics.avgSalaryDueToPaid <= 7 ? "#10b981" : gapMetrics.avgSalaryDueToPaid <= 15 ? "#f59e0b" : "#ef4444",
                  icon: "📅",
                },
                {
                  label: "Salary Paid → Bill Gen",
                  value: gapMetrics.avgSalaryPaidToBillGen !== null ? `${gapMetrics.avgSalaryPaidToBillGen} days` : "N/A",
                  color: gapMetrics.avgSalaryPaidToBillGen === null ? "#9ca3af" : gapMetrics.avgSalaryPaidToBillGen <= 7 ? "#10b981" : gapMetrics.avgSalaryPaidToBillGen <= 15 ? "#f59e0b" : "#ef4444",
                  icon: "📄",
                },
                {
                  label: "Bill Gen → Submission",
                  value: gapMetrics.avgBillGenToSubmission !== null ? `${gapMetrics.avgBillGenToSubmission} days` : "N/A",
                  color: gapMetrics.avgBillGenToSubmission === null ? "#9ca3af" : gapMetrics.avgBillGenToSubmission <= 7 ? "#10b981" : gapMetrics.avgBillGenToSubmission <= 15 ? "#f59e0b" : "#ef4444",
                  icon: "📤",
                },
              ].map((item) => {
                const isHovered = hoveredGapCard === item.label;
                return (
                  <div
                    key={item.label}
                    style={{
                      backgroundColor: item.color,
                      color: "#ffffff",
                      padding: "16px",
                      borderRadius: "12px",
                      boxShadow: isHovered 
                        ? "0 12px 35px rgba(0, 0, 0, 0.25)" 
                        : "0 2px 8px rgba(0, 0, 0, 0.08)",
                      border: isHovered ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.15)",
                      textAlign: "center",
                      position: "relative",
                      minHeight: "120px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: isHovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
                      cursor: "pointer",
                      overflow: "hidden",
                    }}
                    onMouseEnter={() => setHoveredGapCard(item.label)}
                    onMouseLeave={() => setHoveredGapCard(null)}
                  >
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: isHovered 
                        ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" 
                        : "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%)",
                      transition: "all 0.3s ease",
                      pointerEvents: "none",
                    }} />

                    <div style={{
                      fontSize: "24px",
                      marginBottom: "4px",
                      position: "relative",
                      zIndex: 1,
                      transform: isHovered ? "scale(1.1)" : "scale(1)",
                      transition: "transform 0.3s ease",
                    }}>
                      {item.icon}
                    </div>
                    <h6 style={{
                      margin: 0,
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: "500",
                      opacity: 0.9,
                      position: "relative",
                      zIndex: 1,
                      letterSpacing: "0.3px",
                    }}>
                      {item.label}
                    </h6>
                    <p style={{
                      fontSize: isMobile ? "16px" : "20px",
                      margin: "8px 0 0",
                      fontWeight: "bold",
                      position: "relative",
                      zIndex: 1,
                      textShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      letterSpacing: "0.5px",
                    }}>
                      {item.value}
                    </p>
                    <div style={{
                      position: "absolute",
                      bottom: 0,
                      left: isHovered ? "10%" : "50%",
                      right: isHovered ? "10%" : "50%",
                      height: "3px",
                      backgroundColor: "rgba(255,255,255,0.4)",
                      borderRadius: "2px",
                      transition: "all 0.3s ease",
                      opacity: isHovered ? 1 : 0,
                    }} />
                  </div>
                );
              })
            )}
          </div>
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#eaedf2",
            borderRadius: "6px",
            fontSize: "12px",
            color: "#6b7280",
          }}>
            <strong>Note:</strong> Color coding - 🟢 Green: 0-7 days (Good) | 🟡 Yellow: 8-15 days (Moderate) | 🔴 Red: 15+ days (Delayed)
          </div>
        </div>

        {/* Chart Section */}
        <div style={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          padding: isMobile ? "16px" : "24px",
          borderRadius: "16px",
          border: "1px solid #eef2f6",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "box-shadow 0.3s ease",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "12px" : "0",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 4px 12px rgba(74, 144, 217, 0.25)",
              }}>
                📊
              </div>
              <div>
                <h2 style={{
                  fontWeight: 700,
                  fontSize: isMobile ? "17px" : "20px",
                  margin: 0,
                  color: "#1a2332",
                  letterSpacing: "-0.02em",
                }}>
                  Payment vs Receipt Trends
                </h2>
                <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                  Detailed analysis by state
                </p>
              </div>
            </div>
            

          </div>
          <div style={{
            background: "#f8fafc",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid #f0f0f0",
            position: "relative",
            zIndex: 1,
          }}>
            <div style={chartContainerStyle}>
              <canvas
                id="paymentReceiptChart"
                ref={chartRef}
                style={{ width: "100%", height: "100%" }}
              ></canvas>
            </div>
          </div>
        </div>

        {/* Filters Panel - Full Width 2 Row Layout */}
        <div style={{
          background: "#f8fafc",
          padding: isMobile ? "16px" : "20px 24px",
          borderRadius: "12px",
          border: "1px solid #eef2f6",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          transition: "box-shadow 0.3s ease",
        }}>
          
          {/* Filter Title */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            paddingBottom: "12px",
            borderBottom: "1px solid #f0f0f0",
          }}>
            <span style={{ fontSize: "16px" }}>🔍</span>
            <span style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#1a2332",
              letterSpacing: "0.02em",
            }}>Filters</span>
            <span style={{
              fontSize: "11px",
              color: "#94a3b8",
              fontWeight: 400,
              marginLeft: "4px",
            }}>
              {filteredData.length > 0 ? `(${filteredData.length} records)` : ""}
            </span>
          </div>

          {/* First Row - Dropdowns */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(5, 1fr)",
            gap: "12px",
            marginBottom: "14px",
          }}>
            <Select
              value={filters.year || ""}
              onChange={(e) => handleFilterChange("year", e.target.value)}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>📅 Year</MenuItem>
              {uniqueYears.map((year) => (
                <MenuItem key={year} value={year} style={{ fontSize: "13px" }}>
                  {year}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={filters.month || ""}
              onChange={(e) => handleFilterChange("month", e.target.value)}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>📅 Month</MenuItem>
              {uniqueMonths.map((month) => (
                <MenuItem key={month} value={month} style={{ fontSize: "13px" }}>
                  {month}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={filters.state || ""}
              onChange={(e) => handleFilterChange("state", e.target.value)}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>📍 State</MenuItem>
              {uniqueStates.map((state) => (
                <MenuItem key={state} value={state} style={{ fontSize: "13px" }}>
                  {state}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={filters.district || ""}
              onChange={(e) => handleFilterChange("district", e.target.value)}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>🏙️ District</MenuItem>
              {uniqueDistricts.map((district) => (
                <MenuItem key={district} value={district} style={{ fontSize: "13px" }}>
                  {district}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={filters.vc || ""}
              onChange={(e) => handleFilterChange("vc", e.target.value)}
              displayEmpty
              style={{ 
                width: "100%",
                height: "42px",
                fontSize: "13px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  fontSize: '13px',
                  borderRadius: '8px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d9e6',
                  borderRadius: '8px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#4a90d9',
                  borderWidth: '2px',
                },
              }}
            >
              <MenuItem value="" style={{ fontSize: "13px" }}>👤 VC Name</MenuItem>
              {uniqueVCs.map((vc) => (
                <MenuItem key={vc} value={vc} style={{ fontSize: "13px" }}>
                  {vc}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Second Row - Date inputs and Buttons */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr auto auto",
            gap: "12px",
            alignItems: "center",
          }}>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "14px",
                pointerEvents: "none",
              }}>📅</span>
              <input
                type="date"
                value={filters.fromDate || ""}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                placeholder="From Date"
                style={{
                  padding: "8px 14px 8px 38px",
                  border: "1px solid #d1d9e6",
                  borderRadius: "8px",
                  fontSize: "13px",
                  height: "42px",
                  width: "100%",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  outline: "none",
                  color: "#1a2332",
                  cursor: "pointer",
                }}
              />
            </div>

            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "14px",
                pointerEvents: "none",
              }}>📅</span>
              <input
                type="date"
                value={filters.toDate || ""}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
                placeholder="To Date"
                style={{
                  padding: "8px 14px 8px 38px",
                  border: "1px solid #d1d9e6",
                  borderRadius: "8px",
                  fontSize: "13px",
                  height: "42px",
                  width: "100%",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  outline: "none",
                  color: "#1a2332",
                  cursor: "pointer",
                }}
              />
            </div>

            <button
              onClick={applyFilters}
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "0 28px",
                fontSize: "13px",
                fontWeight: 600,
                height: "42px",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                gridColumn: isMobile ? "span 1" : "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>✓</span> Apply
            </button>

            <button
              onClick={resetFilters}
              style={{
                background: "#ffffff",
                color: "#ef4444",
                border: "1.5px solid #ef4444",
                borderRadius: "8px",
                padding: "0 28px",
                fontSize: "13px",
                fontWeight: 600,
                height: "42px",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition: "all 0.25s ease",
                gridColumn: isMobile ? "span 1" : "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>⟳</span> Reset
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div style={tableSectionStyle}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}>
            <h2
              style={{
                fontWeight: "600",
                fontSize: isMobile ? "18px" : "22px",
                margin: 0,
                color: "#1a2332",
                letterSpacing: "-0.01em",
              }}
            >
              Detailed Breakdown of Payment vs Receipt
            </h2>
          </div>
          
          {/* Search and Actions Bar - All in One Row */}
          <div style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            padding: "12px 16px",
            background: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #eef2f6",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            
            {/* Left Side - Search */}
            <div style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flex: "1",
              minWidth: isMobile ? "100%" : "auto",
            }}>
              <div style={{
                position: "relative",
                flex: "1",
                maxWidth: isMobile ? "100%" : "380px",
              }}>
                <span style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by Invoice Number..."
                  value={invoiceSearchInput}
                  onChange={(e) => setInvoiceSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setInvoiceSearch(invoiceSearchInput.trim());
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px 8px 36px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    background: "#ffffff",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4a90d9";
                    e.target.style.boxShadow = "0 0 0 3px rgba(74, 144, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d9e6";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <button
                onClick={() => setInvoiceSearch(invoiceSearchInput.trim())}
                style={{
                  background: "#4a90d9",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 18px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => e.target.style.background = "#357abd"}
                onMouseLeave={(e) => e.target.style.background = "#4a90d9"}
              >
                Search
              </button>
              {invoiceSearch && (
                <button
                  onClick={() => {
                    setInvoiceSearch("");
                    setInvoiceSearchInput("");
                  }}
                  style={{
                    background: "transparent",
                    color: "#ef4444",
                    border: "1px solid #ef4444",
                    borderRadius: "6px",
                    padding: "7px 16px",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#ef4444";
                    e.target.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#ef4444";
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>
          </div>

          <TableContainer
            component={Paper}
            style={{
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "600px",
              borderRadius: "10px",
              border: "1px solid #eef2f6",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Table style={{ minWidth: 650 }} aria-label="payment vs receipt table" stickyHeader>
              <TableHead>
                <TableRow>
                  {(() => {
                    const headers = [
                      "Select",
                      "Invoice Number",
                      "Month",
                      "Year",
                      "State",
                      "District",
                      "VC Name",
                      "Total Salary Paid (₹)",
                      "Invoice Amount (₹)",
                      "Payment Received Excl. TDS (₹)",
                      "TDS (₹)",
                      "Payment Received Incl. TDS (₹)",
                      "Deductions (₹)",
                      "Excess Receipt (₹)",
                      "Excess Receipt % on Salary Paid",
                    ];
                    return headers.map((header, index) => (
                      <TableCell
                        key={index}
                        style={{
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "12px 16px",
                          cursor: index !== 0 ? "pointer" : "default",
                          backgroundColor: "#f8fafc",
                          borderBottom: "2px solid #eef2f6",
                          position: "sticky",
                          top: 0,
                          zIndex: index === 0 ? 3 : 1,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() =>
                          index !== 0 &&
                          handleSort(
                            index === 1
                              ? "invoice_no"
                              : index === 2
                              ? "salary_month"
                              : index === 3
                              ? "year"
                              : index === 4
                              ? "state"
                              : index === 5
                              ? "district"
                              : index === 6
                              ? "vc_name"
                              : index === 7
                              ? "total_salary_paid"
                              : index === 8
                              ? "total_invoice_amount"
                              : index === 9
                              ? "total_payment_received"
                              : index === 10
                              ? "tds_deducted"
                              : index === 11
                              ? "total_payment_received_incl_tds"
                              : index === 12
                              ? "total_deductions"
                              : index === 13
                              ? "excess_receipt"
                              : "excess_receipt_percent",
                            index
                          )
                        }
                        onMouseEnter={(e) => {
                          if (index !== 0) {
                            e.target.style.color = "#1a2332";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (index !== 0) {
                            e.target.style.color = "#475569";
                          }
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {index === 0 ? (
                            <Checkbox
                              checked={paginatedRows.length > 0 && paginatedRows.every(row => selectedInvoiceNos.includes(row.invoiceNo))}
                              indeterminate={paginatedRows.some(row => selectedInvoiceNos.includes(row.invoiceNo)) && !paginatedRows.every(row => selectedInvoiceNos.includes(row.invoiceNo))}
                              onChange={handleSelectAll}
                              size="small"
                            />
                          ) : (
                            <>
                              {header}
                              {sortConfig.key === (
                                index === 1
                                  ? "invoice_no"
                                  : index === 2
                                  ? "salary_month"
                                  : index === 3
                                  ? "year"
                                  : index === 4
                                  ? "state"
                                  : index === 5
                                  ? "district"
                                  : index === 6
                                  ? "vc_name"
                                  : index === 7
                                  ? "total_salary_paid"
                                  : index === 8
                                  ? "total_invoice_amount"
                                  : index === 9
                                  ? "total_payment_received"
                                  : index === 10
                                  ? "tds_deducted"
                                  : index === 11
                                  ? "total_payment_received_incl_tds"
                                  : index === 12
                                  ? "total_deductions"
                                  : index === 13
                                  ? "excess_receipt"
                                  : "excess_receipt_percent"
                              ) && (
                                <span style={{ fontSize: "10px", color: "#4a90d9" }}>
                                  {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    ));
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    style={{ transition: "background-color 0.15s ease", cursor: "pointer" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#edf2f7";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    onClick={() => handleSelectRow(row.invoiceNo)}
                  >
                    <TableCell style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0" }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedInvoiceNos.includes(row.invoiceNo)}
                        onChange={() => handleSelectRow(row.invoiceNo)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.invoiceNo}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.month}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.year}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.state}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.district}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.vc_name}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.totalSalaryPaid}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.invoiceAmount}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.paymentReceived}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.tdsDeducted}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {formatCurrency(
                        Number(row.tdsDeducted.replace(/[^0-9.-]+/g, "")) +
                          Number(row.paymentReceived.replace(/[^0-9.-]+/g, ""))
                      )}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.deductions}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#1a2332", fontWeight: 500 }}>
                      {row.excessReceipt}
                    </TableCell>
                    <TableCell style={{ padding: "10px 16px", fontSize: "13px", borderBottom: "1px solid #f0f0f0", color: "#475569" }}>
                      {row.excessReceiptPercent}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Custom Pagination Toolbar exactly matching SalaryPayment.js and InvoiceReceipt.js */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            borderTop: "1px solid #eef2f6",
            background: "#f1f5f9",
            borderRadius: "0 0 10px 10px",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "8px" : "0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: "#64748b" }}>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d1d9e6",
                  borderRadius: "4px",
                  fontSize: "13px",
                  background: "#fff",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {[10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                {currentPage * rowsPerPage + 1}-
                {Math.min((currentPage + 1) * rowsPerPage, totalRows)} of {totalRows}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  onClick={() => handleChangePage(0)}
                  disabled={currentPage === 0}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "4px",
                    background: "#fff",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.4 : 1,
                    color: "#475569",
                    fontSize: "12px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 0) e.target.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                  }}
                >
                  ⟪
                </button>
                <button
                  onClick={() => handleChangePage(currentPage - 1)}
                  disabled={currentPage === 0}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "4px",
                    background: "#fff",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.4 : 1,
                    color: "#475569",
                    fontSize: "12px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 0) e.target.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                  }}
                >
                  ⟨
                </button>
                <button
                  onClick={() => handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPages - 1 || totalPages === 0}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "4px",
                    background: "#fff",
                    cursor: (currentPage === totalPages - 1 || totalPages === 0) ? "not-allowed" : "pointer",
                    opacity: (currentPage === totalPages - 1 || totalPages === 0) ? 0.4 : 1,
                    color: "#475569",
                    fontSize: "12px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage < totalPages - 1) e.target.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                  }}
                >
                  ⟩
                </button>
                <button
                  onClick={() => handleChangePage(totalPages - 1)}
                  disabled={currentPage === totalPages - 1 || totalPages === 0}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d9e6",
                    borderRadius: "4px",
                    background: "#fff",
                    cursor: (currentPage === totalPages - 1 || totalPages === 0) ? "not-allowed" : "pointer",
                    opacity: (currentPage === totalPages - 1 || totalPages === 0) ? 0.4 : 1,
                    color: "#475569",
                    fontSize: "12px",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage < totalPages - 1) e.target.style.background = "#f1f5f9";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                  }}
                >
                  ⟫
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Export Data Section - Premium Design matching InvoiceReceipt.js */}
        <div style={{
          background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
          padding: isMobile ? "20px" : "28px",
          marginTop: "24px",
          borderRadius: "20px",
          border: "1px solid rgba(238, 242, 246, 0.8)",
          marginBottom: "24px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)",
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 12px 48px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.borderColor = "#d1d9e6";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.8)";
        }}
        >
          {/* Animated Gradient Background */}
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-30%",
            width: "60%",
            height: "100%",
            background: "radial-gradient(circle, rgba(74, 144, 217, 0.03) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />

          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
            paddingBottom: "20px",
            borderBottom: "1px solid rgba(238, 242, 246, 0.6)",
            position: "relative",
            zIndex: 1,
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "12px" : "0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{
                width: "52px",
                height: "52px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #4a90d9 0%, #357abd 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 8px 24px rgba(74, 144, 217, 0.25)",
                position: "relative",
              }}>
                💾
              </div>
              <div>
                <h3 style={{
                  fontWeight: 700,
                  fontSize: isMobile ? "17px" : "20px",
                  margin: 0,
                  color: "#1a2332",
                  letterSpacing: "-0.02em",
                  background: "linear-gradient(135deg, #1a2332 0%, #4a90d9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  Export Data
                </h3>
                <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>📋</span>
                  {displayData.length} records ready to export
                </p>
              </div>
            </div>
            
            {/* Quick Stats Badge */}
            <div style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              padding: "6px 16px",
              borderRadius: "100px",
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#10b981",
                display: "inline-block",
              }} />
              <span style={{ fontSize: "12px", color: "#475569", fontWeight: 500 }}>
                Ready to export
              </span>
            </div>
          </div>

          {/* Export Options - Modern Card Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: "16px",
            position: "relative",
            zIndex: 1,
          }}>
            
            {/* CSV Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(74, 144, 217, 0.15)";
              e.currentTarget.style.borderColor = "#4a90d9";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={exportToCSV}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📄
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>CSV</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Spreadsheet format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(74, 144, 217, 0.08)",
                fontSize: "11px",
                color: "#4a90d9",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* PDF Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(239, 68, 68, 0.12)";
              e.currentTarget.style.borderColor = "#ef4444";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={exportToPDF}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📄
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>PDF</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Document format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(239, 68, 68, 0.08)",
                fontSize: "11px",
                color: "#ef4444",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* Excel Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(16, 185, 129, 0.12)";
              e.currentTarget.style.borderColor = "#10b981";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={exportToCSV}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                📊
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>Excel</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Spreadsheet format</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(16, 185, 129, 0.08)",
                fontSize: "11px",
                color: "#10b981",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Download
              </div>
            </div>

            {/* Print Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              padding: "20px 16px",
              border: "1px solid rgba(238, 242, 246, 0.6)",
              textAlign: "center",
              transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 16px 40px rgba(139, 92, 246, 0.12)";
              e.currentTarget.style.borderColor = "#8b5cf6";
              e.currentTarget.style.background = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.02)";
              e.currentTarget.style.borderColor = "rgba(238, 242, 246, 0.6)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            }}
            onClick={handlePrint}
            >
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "24px",
                transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.15) rotate(-5deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1) rotate(0deg)";
              }}
              >
                🖨️
              </div>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1a2332", margin: "0 0 4px 0" }}>Print</h4>
              <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0, lineHeight: 1.4 }}>Printer friendly layout</p>
              <div style={{
                marginTop: "14px",
                padding: "6px 16px",
                borderRadius: "100px",
                background: "rgba(139, 92, 246, 0.08)",
                fontSize: "11px",
                color: "#8b5cf6",
                fontWeight: 600,
                display: "inline-block",
              }}>
                ⬇ Print
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentVsReceipt;
